import { getProcessedValueKey } from '@databases/database-ai-first-creation-state';
import { proxy } from 'valtio';

type MutableAiSummarySettlementState = {
  pendingValues: Set<string>;
  settledValues: Set<string>;
  settlingValues: Set<string>;
};

declare const aiSummarySettlementRunIdBrand: unique symbol;

type AiSummarySettlementRunId = string & {
  readonly [aiSummarySettlementRunIdBrand]: true;
};

export type AiSummarySettlementRunToken = Readonly<{
  runId: AiSummarySettlementRunId;
  valueKeysByEntryId: ReadonlyMap<string, string>;
}>;

const mutableAiSummarySettlementState = proxy<MutableAiSummarySettlementState>({
  pendingValues: new Set<string>(),
  settledValues: new Set<string>(),
  settlingValues: new Set<string>()
});

export const aiSummarySettlementState: Readonly<MutableAiSummarySettlementState> =
  mutableAiSummarySettlementState;

const activeRunIdsByValueKey = new Map<string, AiSummarySettlementRunId>();

const clearValueState = (valueKey: string): void => {
  mutableAiSummarySettlementState.pendingValues.delete(valueKey);
  mutableAiSummarySettlementState.settlingValues.delete(valueKey);
  mutableAiSummarySettlementState.settledValues.delete(valueKey);
};

const getOwnedValueKeys = (
  run: AiSummarySettlementRunToken,
  entryIds: readonly string[]
): string[] => {
  const valueKeys: string[] = [];
  for (const entryId of entryIds) {
    const valueKey = run.valueKeysByEntryId.get(entryId);
    if (valueKey !== undefined && activeRunIdsByValueKey.get(valueKey) === run.runId) {
      valueKeys.push(valueKey);
    }
  }
  return valueKeys;
};

const setValuePhase = (
  valueKey: string,
  phase: 'pending' | 'settled' | 'settling'
): void => {
  clearValueState(valueKey);
  if (phase === 'pending') {
    mutableAiSummarySettlementState.pendingValues.add(valueKey);
  } else if (phase === 'settling') {
    mutableAiSummarySettlementState.settlingValues.add(valueKey);
  } else {
    mutableAiSummarySettlementState.settledValues.add(valueKey);
  }
};

export const aiSummarySettlementStateManager = {
  begin(fieldId: string, entryIds: readonly string[]): AiSummarySettlementRunToken {
    const runId = globalThis.crypto.randomUUID() as AiSummarySettlementRunId;
    const valueKeysByEntryId = new Map(
      entryIds.map((entryId) => [entryId, getProcessedValueKey(fieldId, entryId)])
    );
    for (const valueKey of valueKeysByEntryId.values()) {
      activeRunIdsByValueKey.set(valueKey, runId);
      clearValueState(valueKey);
    }

    return { runId, valueKeysByEntryId };
  },
  markPending(run: AiSummarySettlementRunToken, entryIds: readonly string[]): void {
    for (const valueKey of getOwnedValueKeys(run, entryIds)) {
      setValuePhase(valueKey, 'pending');
    }
  },
  settle(run: AiSummarySettlementRunToken, entryIds: readonly string[]): void {
    for (const valueKey of getOwnedValueKeys(run, entryIds)) {
      setValuePhase(valueKey, 'settling');
    }
  },
  finish(run: AiSummarySettlementRunToken, settledEntryIds: readonly string[]): void {
    const settledValueKeys = new Set(getOwnedValueKeys(run, settledEntryIds));
    for (const valueKey of run.valueKeysByEntryId.values()) {
      if (activeRunIdsByValueKey.get(valueKey) !== run.runId) {
        continue;
      }
      clearValueState(valueKey);
      if (settledValueKeys.has(valueKey)) {
        setValuePhase(valueKey, 'settled');
      } else {
        activeRunIdsByValueKey.delete(valueKey);
      }
    }

    globalThis.setTimeout(() => {
      for (const valueKey of settledValueKeys) {
        if (activeRunIdsByValueKey.get(valueKey) === run.runId) {
          mutableAiSummarySettlementState.settledValues.delete(valueKey);
          activeRunIdsByValueKey.delete(valueKey);
        }
      }
    }, 0);
  },
  cancel(run: AiSummarySettlementRunToken): void {
    for (const valueKey of run.valueKeysByEntryId.values()) {
      if (activeRunIdsByValueKey.get(valueKey) === run.runId) {
        clearValueState(valueKey);
        activeRunIdsByValueKey.delete(valueKey);
      }
    }
  }
};
